import { useCallback, useEffect, useRef, useState } from 'react';

import ModalShell from './ModalShell';
import {
  loadResumePreview,
  resumeDisplayName,
  revokePreviewUrl,
  resolveResumeDownloadBlob,
  triggerFileDownload
} from '../utils/resumePreview';

function EyeIcon({ className = 'w-5 h-5' }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );
}

/** Above staff candidate modal (14000) and job match modals (12000–13000). */
const RESUME_PREVIEW_Z = 'z-[15000]';

export function ResumePreviewModal({
  open,
  onClose,
  src,
  file,
  fileName,
  candidateName,
  candidateId,
  zIndex = RESUME_PREVIEW_Z
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [downloadReady, setDownloadReady] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const downloadMetaRef = useRef(null);

  useEffect(() => {
    if (!open) {
      revokePreviewUrl(preview);
      setPreview(null);
      setError('');
      downloadMetaRef.current = null;
      setDownloadReady(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    loadResumePreview({ src, file, fileName })
      .then(data => {
        if (!cancelled) {
          setPreview(data);
          if (data?.blob) {
            downloadMetaRef.current = null;
            resolveResumeDownloadBlob({
              src,
              file,
              fileName,
              candidateName,
              candidateId,
              cachedBlob: data.blob
            })
              .then(meta => {
                if (!cancelled) {
                  downloadMetaRef.current = meta;
                  setDownloadReady(true);
                }
              })
              .catch(() => {
                if (!cancelled) setDownloadReady(false);
              });
          }
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Failed to load preview');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, src, file, fileName, candidateName, candidateId]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    downloadMetaRef.current = null;
    setDownloadReady(false);

    resolveResumeDownloadBlob({ src, file, fileName, candidateName, candidateId })
      .then(meta => {
        if (!cancelled) {
          downloadMetaRef.current = meta;
          setDownloadReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          downloadMetaRef.current = null;
          setDownloadReady(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, src, file, fileName, candidateName, candidateId]);

  useEffect(
    () => () => {
      revokePreviewUrl(preview);
    },
    [preview]
  );

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const meta =
        downloadMetaRef.current ||
        (await resolveResumeDownloadBlob({
          src,
          file,
          fileName,
          candidateName,
          candidateId,
          cachedBlob: preview?.blob
        }));
      triggerFileDownload(meta.blob, meta.fileName);
    } catch (err) {
      window.alert(err.response?.data?.message || err.message || 'Could not download resume');
    } finally {
      setDownloading(false);
    }
  }, [src, file, fileName, candidateName, candidateId, preview?.blob]);

  if (!open) return null;

  const label = resumeDisplayName(src, fileName);

  const downloadButton = (
    <button
      type="button"
      className="text-sm text-[hsl(var(--ds-fg))] hover:text-[hsl(var(--ds-fg))] font-medium border border-[hsl(var(--ds-border))] px-3 py-1.5 bg-[hsl(var(--ds-surface))] disabled:opacity-50 rounded-lg"
      disabled={downloading}
      onClick={handleDownload}
    >
      {downloading ? 'Downloading…' : 'Download'}
    </button>
  );

  return (
    <ModalShell
      title="Resume preview"
      subtitle={label !== 'Resume' ? label : undefined}
      onClose={onClose}
      maxWidth="max-w-5xl"
      zIndex={zIndex}
      footer={<div className="flex items-center gap-3">{downloadButton}</div>}
    >
      <div className="h-[min(75vh,42rem)] bg-[hsl(var(--ds-muted))] border border-[hsl(var(--ds-border))] overflow-hidden rounded-lg">
        {loading ? (
          <div className="h-full flex items-center justify-center text-sm text-[hsl(var(--ds-muted-fg))]">Loading preview…</div>
        ) : error ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center text-sm text-red-600">
            <p>{error}</p>
            {downloadReady && (
              <button type="button" className="text-[hsl(var(--ds-primary))] hover:underline font-medium" onClick={handleDownload}>
                Download file instead
              </button>
            )}
          </div>
        ) : preview?.kind === 'pdf' && preview.href ? (
          <iframe src={preview.href} title="Resume preview" className="w-full h-full border-0 bg-[hsl(var(--ds-surface))]" />
        ) : preview?.kind === 'html' && preview.html ? (
          <div
            className="w-full h-full overflow-y-auto bg-[hsl(var(--ds-surface))] p-6 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: preview.html }}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-[hsl(var(--ds-muted-fg))]">No preview available</div>
        )}
      </div>
    </ModalShell>
  );
}

/**
 * Compact resume bar with eye button (preview opens only when clicked).
 */
export default function ResumePreviewFrame({
  src,
  file,
  fileName,
  candidateName,
  candidateId,
  modalOpen,
  onModalOpenChange,
  className = '',
  compact = false,
  iconOnly = false
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const hasPreview = Boolean(src || file);
  if (!hasPreview) return null;

  const label = resumeDisplayName(src, fileName);
  const isControlled = modalOpen !== undefined && typeof onModalOpenChange === 'function';
  const popupOpen = isControlled ? modalOpen : internalOpen;

  const setPopupOpen = next => {
    if (isControlled) onModalOpenChange(next);
    else setInternalOpen(next);
  };

  const eyeButton = (
    <button
      type="button"
      className={
        iconOnly
          ? `inline-flex items-center justify-center text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-primary))] shrink-0 p-1 rounded-lg hover:bg-[hsl(var(--ds-muted))]/80 transition-colors ${className}`
          : `inline-flex items-center justify-center text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] rounded-sm shrink-0 ${compact ? 'w-8 h-8' : 'w-9 h-9'}`
      }
      onClick={() => setPopupOpen(true)}
      title={`Preview resume${label ? `: ${label}` : ''}`}
      aria-label="Preview resume"
    >
      <EyeIcon className={iconOnly ? 'w-5 h-5' : compact ? 'w-4 h-4' : 'w-5 h-5'} />
    </button>
  );

  if (iconOnly) {
    return (
      <>
        {eyeButton}
        <ResumePreviewModal
          open={popupOpen}
          onClose={() => setPopupOpen(false)}
          src={src}
          file={file}
          fileName={fileName}
          candidateName={candidateName}
          candidateId={candidateId}
        />
      </>
    );
  }

  return (
    <>
      <div className={`border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] ${className}`}>
        <div className={`flex items-center justify-between gap-2 ${compact ? 'px-2 py-1.5' : 'px-3 py-2'}`}>
          <span className={`text-[hsl(var(--ds-fg))] truncate min-w-0 ${compact ? 'text-xs' : 'text-sm'}`} title={label}>
            {label}
          </span>
          {eyeButton}
        </div>
        {!compact && (
          <p className="px-3 pb-2 text-xs text-[hsl(var(--ds-muted-fg))]">Click the eye icon to preview (PDF, Word, plain text).</p>
        )}
      </div>

      <ResumePreviewModal
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        src={src}
        file={file}
        fileName={fileName}
        candidateName={candidateName}
        candidateId={candidateId}
      />
    </>
  );
}
